<template>
  <div class="popup-form quick-deal-popup">
    <div class="content-form-popup quick-deal-content">
      <header class="quick-deal-header">
        <button class="quick-deal-close" type="button" aria-label="Zavrieť" @click="closePopup">
          <img class="filter-primary" src="@/images/modal-close.svg" alt=""/>
        </button>
        <div>
          <span class="quick-deal-kicker">Rýchly zápis obchodu</span>
          <h2>{{ clientName }}</h2>
          <p v-if="!result">
            Evidencia už načerpanej hypotéky bez prepočtu a prechádzania celým procesom.
          </p>
        </div>
      </header>

      <div v-if="result" class="quick-deal-success">
        <div class="quick-deal-success-icon">✓</div>
        <h3>Obchod bol zaevidovaný</h3>
        <p>
          Hypotéka #{{ result.mortgage.id }} aj žiadosť sú v servise a klientovi bol
          vytvorený aktívny hypotekárny úver.
        </p>
        <div class="quick-deal-success-summary">
          <span>{{ selectedBankName }}</span>
          <strong>{{ formatMoney(form.suma) }} EUR</strong>
          <span>{{ form.sadzba }} % · čerpanie {{ formatDate(form.nacerpana) }}</span>
        </div>
        <div class="quick-deal-success-actions">
          <button type="button" class="quick-deal-secondary" @click="closePopup">Hotovo</button>
          <button type="button" class="quick-deal-primary" @click="openMortgage">
            Otvoriť hypotéku
          </button>
        </div>
      </div>

      <form v-else @submit.prevent="submit">
        <div class="quick-deal-info">
          <span class="quick-deal-info-icon">i</span>
          <p>
            Uložením sa vytvorí hypotéka v <strong>servise</strong>, žiadosť v stave
            <strong>načerpaná</strong> a aktívny dlh klienta. Dátum čerpania sa použije
            aj pre evidenciu dokončeného obchodu.
          </p>
        </div>

        <div class="quick-deal-section-title">Základné údaje zmluvy</div>
        <div class="quick-deal-grid">
          <label class="quick-deal-field quick-deal-field--wide">
            <span>Banka *</span>
            <select v-model.number="form.bank" required>
              <option :value="null" disabled>Vyberte banku</option>
              <option v-for="bank in banks" :key="bank.id" :value="bank.id">
                {{ bank.nazov }}
              </option>
            </select>
          </label>

          <label class="quick-deal-field">
            <span>Účel *</span>
            <select v-model="form.ucel" required>
              <option v-for="purpose in purposes" :key="purpose.value" :value="purpose.value">
                {{ purpose.label }}
              </option>
            </select>
          </label>

          <label class="quick-deal-field">
            <span>Výška úveru *</span>
            <div class="quick-deal-input-suffix">
              <input v-model.number="form.suma" type="number" min="0.01" step="0.01" required/>
              <em>EUR</em>
            </div>
          </label>

          <label class="quick-deal-field">
            <span>Schválená sadzba *</span>
            <div class="quick-deal-input-suffix">
              <input v-model.number="form.sadzba" type="number" min="0" max="99.99" step="0.01" required/>
              <em>%</em>
            </div>
          </label>

          <label class="quick-deal-field">
            <span>Mesačná splátka *</span>
            <div class="quick-deal-input-suffix">
              <input v-model.number="form.splatka" type="number" min="0.01" step="0.01" required/>
              <em>EUR</em>
            </div>
          </label>

          <label class="quick-deal-field">
            <span>Splatnosť *</span>
            <div class="quick-deal-input-suffix">
              <input v-model.number="form.splatnost" type="number" min="1" max="40" step="1" required/>
              <em>rokov</em>
            </div>
          </label>

          <label class="quick-deal-field">
            <span>Fixácia *</span>
            <div class="quick-deal-input-suffix">
              <input v-model.number="form.fix" type="number" min="1" max="40" step="1" required/>
              <em>rokov</em>
            </div>
          </label>
        </div>

        <div class="quick-deal-section-title">Dokončenie a doplnkové údaje</div>
        <div class="quick-deal-grid">
          <label class="quick-deal-field quick-deal-field--important">
            <span>Dátum načerpania *</span>
            <input v-model="form.nacerpana" type="date" :max="today" required/>
          </label>

          <label class="quick-deal-field">
            <span>Deň splátky *</span>
            <select v-model.number="form.preferred_payment_day" required>
              <option :value="null" disabled>Vyberte deň</option>
              <option v-for="day in paymentDays" :key="day" :value="day">{{ day }}. deň</option>
            </select>
          </label>

          <label class="quick-deal-field">
            <span>Číslo úveru / zmluvy</span>
            <input v-model.trim="form.cislo_uveru" type="text" maxlength="64" placeholder="Voliteľné"/>
          </label>

          <label class="quick-deal-field">
            <span>Poplatok za poskytnutie</span>
            <div class="quick-deal-input-suffix">
              <input v-model.number="form.poplatok_za_poskytnutie" type="number" min="0" step="0.01" placeholder="0"/>
              <em>EUR</em>
            </div>
          </label>

          <label class="quick-deal-field quick-deal-field--wide">
            <span>Interná poznámka</span>
            <textarea
              v-model.trim="form.poznamka"
              rows="3"
              placeholder="Napr. obchod evidovaný spätne, kontakt v banke..."
            ></textarea>
          </label>
        </div>

        <div class="quick-deal-section-title">Dokumentácia obchodu</div>
        <div class="quick-deal-record-upload">
          <div>
            <strong>Záznam o sprostredkovaní / potrebách klienta</strong>
            <p>Voliteľne priložte podpísaný dokument. Uloží sa priamo k vytvorenej žiadosti a zobrazí sa aj v checkliste čerpania.</p>
          </div>
          <label v-if="!signedClientNeedsRecord" class="quick-deal-file-button">
            <input
              ref="signedClientNeedsRecordInput"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.asice"
              @change="selectSignedClientNeedsRecord"
            />
            Nahrať dokument
          </label>
          <div v-else class="quick-deal-selected-file">
            <span>{{ signedClientNeedsRecord.name }}</span>
            <button type="button" @click="removeSignedClientNeedsRecord">Odobrať</button>
          </div>
        </div>
        <p v-if="recordError" class="quick-deal-record-error" role="alert">{{ recordError }}</p>

        <div v-if="errors.length" class="quick-deal-errors" role="alert">
          <strong>Skontrolujte zadané údaje:</strong>
          <span v-for="(error, index) in errors" :key="index">{{ error }}</span>
        </div>

        <footer class="quick-deal-footer">
          <button type="button" class="quick-deal-secondary" :disabled="saving" @click="closePopup">
            Zrušiť
          </button>
          <button type="submit" class="quick-deal-primary" :disabled="saving || !canSubmit">
            <span v-if="saving">Evidujem obchod...</span>
            <span v-else>Zaevidovať načerpaný obchod</span>
          </button>
        </footer>
      </form>
    </div>
  </div>
</template>

<script>
import {createQuickProcessedMortgage} from "@/services/brokerService";

export default {
  name: "QuickProcessedMortgagePopUp",
  props: {
    payload: {
      type: Object,
      required: true,
    },
  },
  data() {
    const today = this.localDate(new Date());
    return {
      today,
      saving: false,
      result: null,
      errors: [],
      recordError: "",
      signedClientNeedsRecord: null,
      purposes: [
        {value: "nadobudnutie", label: "Nadobudnutie"},
        {value: "refinancovanie", label: "Refinancovanie"},
        {value: "bezucel", label: "Bezúčelový úver"},
      ],
      paymentDays: [5, 10, 15, 20, 25],
      form: {
        bank: null,
        ucel: "nadobudnutie",
        suma: null,
        sadzba: null,
        splatka: null,
        splatnost: 30,
        fix: 3,
        nacerpana: today,
        preferred_payment_day: null,
        poplatok_za_poskytnutie: null,
        cislo_uveru: "",
        poznamka: "",
      },
    };
  },
  computed: {
    banks() {
      return this.$store.getters["formStore/getBanks"] || [];
    },
    clientName() {
      const name = `${this.payload.first_name || ""} ${this.payload.last_name || ""}`.trim();
      return name || `Klient #${this.payload.id}`;
    },
    selectedBankName() {
      const bank = this.banks.find(item => item.id === this.form.bank);
      return bank ? bank.nazov : "Banka";
    },
    canSubmit() {
      return Boolean(
        this.form.bank &&
        this.form.ucel &&
        Number(this.form.suma) > 0 &&
        Number(this.form.sadzba) >= 0 &&
        Number(this.form.splatka) > 0 &&
        Number(this.form.splatnost) > 0 &&
        Number(this.form.fix) > 0 &&
        Number(this.form.preferred_payment_day) > 0 &&
        this.form.nacerpana
      );
    },
  },
  methods: {
    localDate(date) {
      const offset = date.getTimezoneOffset();
      return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
    },
    normalizedPayload() {
      return {
        ...this.form,
        preferred_payment_day: this.form.preferred_payment_day || null,
        poplatok_za_poskytnutie:
          this.form.poplatok_za_poskytnutie === "" ? null : this.form.poplatok_za_poskytnutie,
        cislo_uveru: this.form.cislo_uveru || null,
        poznamka: this.form.poznamka || null,
      };
    },
    flattenErrors(data) {
      if (!data) return ["Obchod sa nepodarilo uložiť."];
      if (Array.isArray(data)) return data.flatMap(item => this.flattenErrors(item));
      if (typeof data === "object") {
        return Object.entries(data).flatMap(([field, value]) =>
          this.flattenErrors(value).map(message => `${field}: ${message}`)
        );
      }
      return [String(data)];
    },
    async submit() {
      if (!this.canSubmit || this.saving) return;
      this.saving = true;
      this.errors = [];
      try {
        this.result = await createQuickProcessedMortgage(
          this.payload.id,
          this.normalizedPayload(),
          this.signedClientNeedsRecord
        );
        this.$store.commit("popupManagerState/setUserManagerRefresh", true);
        this.$store.dispatch("clientState/refreshClientsData").catch(() => null);
        this.$store.commit("appState/setSuccessMessage", {
          text: "Načerpaný obchod bol úspešne zaevidovaný",
          type: "success",
        });
      } catch (error) {
        this.errors = this.flattenErrors(error?.response?.data);
      } finally {
        this.saving = false;
      }
    },
    selectSignedClientNeedsRecord(event) {
      const file = event.target.files?.[0];
      this.recordError = "";
      if (!file) return;
      const extension = file.name.split(".").pop()?.toLowerCase();
      const supported = ["pdf", "png", "jpg", "jpeg", "doc", "docx", "asice"];
      if (!supported.includes(extension)) {
        this.recordError = "Vyberte PDF, obrázok, Word alebo ASiC-E dokument.";
        event.target.value = "";
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        this.recordError = "Dokument môže mať najviac 25 MB.";
        event.target.value = "";
        return;
      }
      this.signedClientNeedsRecord = file;
    },
    removeSignedClientNeedsRecord() {
      this.signedClientNeedsRecord = null;
      this.recordError = "";
      if (this.$refs.signedClientNeedsRecordInput) {
        this.$refs.signedClientNeedsRecordInput.value = "";
      }
    },
    closePopup() {
      this.$store.commit("popupManagerState/popItemFromPopupOrder");
    },
    openMortgage() {
      this.$router.push({
        name: "KartaHypotekyDetail",
        params: {hypoteka: this.result.mortgage.id},
      });
      this.$store.commit("popupManagerState/resetPopupOrder");
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("sk-SK", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatDate(value) {
      if (!value) return "";
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.quick-deal-content {
  width: min(900px, 100%);
  margin: 0 auto;
  padding: 30px;
}

.quick-deal-header {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 15px;
  margin-bottom: 22px;
}

.quick-deal-close {
  display: grid;
  width: 38px;
  height: 38px;
  padding: 0;
  place-items: center;
  border: 1px solid var(--app-border);
  border-radius: 11px;
  background: var(--app-surface-muted);
  cursor: pointer;
}

.quick-deal-close img {
  width: 17px;
  height: 17px;
}

.quick-deal-kicker {
  color: var(--app-primary);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.quick-deal-header h2,
.quick-deal-success h3 {
  margin: 3px 0 5px;
  color: var(--app-text);
  font-size: 22px;
  font-weight: 800;
}

.quick-deal-header p,
.quick-deal-success p {
  margin: 0;
  color: var(--app-text-muted);
  font-size: 13px;
}

.quick-deal-info {
  display: flex;
  gap: 11px;
  margin-bottom: 24px;
  padding: 13px 15px;
  border: 1px solid rgba(42, 139, 242, .22);
  border-radius: 12px;
  background: rgba(42, 139, 242, .07);
  color: var(--app-text);
}

.quick-deal-info p {
  margin: 0;
  font-size: 12px;
  line-height: 1.55;
}

.quick-deal-info-icon {
  display: grid;
  flex: 0 0 22px;
  height: 22px;
  place-items: center;
  border-radius: 50%;
  background: var(--app-primary);
  color: white;
  font-size: 11px;
  font-weight: 800;
}

.quick-deal-section-title {
  margin: 20px 0 11px;
  color: var(--app-text);
  font-size: 12px;
  font-weight: 800;
}

.quick-deal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.quick-deal-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
}

.quick-deal-field--wide {
  grid-column: 1 / -1;
}

.quick-deal-field > span {
  color: var(--app-text-muted);
  font-size: 10px;
  font-weight: 700;
}

.quick-deal-field input,
.quick-deal-field select,
.quick-deal-field textarea {
  width: 100%;
  min-height: 42px;
  padding: 10px 12px;
  border: 1px solid var(--app-border);
  border-radius: 10px;
  background: var(--app-surface);
  color: var(--app-text);
  font: inherit;
  font-size: 13px;
}

.quick-deal-field textarea {
  resize: vertical;
}

.quick-deal-field input:focus,
.quick-deal-field select:focus,
.quick-deal-field textarea:focus {
  border-color: var(--app-primary);
  outline: none;
  box-shadow: 0 0 0 3px rgba(42, 139, 242, .1);
}

.quick-deal-field--important input {
  border-color: rgba(32, 161, 52, .45);
  background: rgba(32, 161, 52, .05);
}

.quick-deal-input-suffix {
  position: relative;
}

.quick-deal-input-suffix input {
  padding-right: 58px;
}

.quick-deal-input-suffix em {
  position: absolute;
  top: 50%;
  right: 12px;
  color: var(--app-text-muted);
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  transform: translateY(-50%);
}

.quick-deal-errors {
  display: flex;
  margin-top: 18px;
  padding: 12px 14px;
  flex-direction: column;
  gap: 3px;
  border: 1px solid rgba(220, 53, 69, .28);
  border-radius: 10px;
  background: rgba(220, 53, 69, .07);
  color: #b42331;
  font-size: 11px;
}

.quick-deal-record-upload {
  display: flex;
  min-height: 82px;
  padding: 14px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px dashed rgba(42, 139, 242, .5);
  border-radius: 12px;
  background: rgba(42, 139, 242, .05);
}

.quick-deal-record-upload strong {
  color: var(--app-text);
  font-size: 12px;
}

.quick-deal-record-upload p {
  max-width: 520px;
  margin: 4px 0 0;
  color: var(--app-text-muted);
  font-size: 10px;
  line-height: 1.45;
}

.quick-deal-file-button,
.quick-deal-selected-file {
  display: flex;
  min-height: 40px;
  padding: 9px 13px;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--app-border);
  border-radius: 10px;
  background: var(--app-surface);
  color: var(--app-text);
  font-size: 11px;
  font-weight: 800;
}

.quick-deal-file-button {
  flex: 0 0 auto;
  cursor: pointer;
}

.quick-deal-file-button input {
  display: none;
}

.quick-deal-selected-file {
  min-width: 0;
  max-width: 320px;
}

.quick-deal-selected-file span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quick-deal-selected-file button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #e74c64;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
}

.quick-deal-record-error {
  margin: 7px 2px 0;
  color: #e74c64;
  font-size: 10px;
  font-weight: 700;
}

.quick-deal-footer,
.quick-deal-success-actions {
  display: flex;
  margin-top: 24px;
  justify-content: flex-end;
  gap: 10px;
}

.quick-deal-primary,
.quick-deal-secondary {
  min-height: 42px;
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.quick-deal-primary {
  border: 1px solid var(--app-primary);
  background: var(--app-primary);
  color: white;
}

.quick-deal-secondary {
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-text);
}

.quick-deal-primary:disabled,
.quick-deal-secondary:disabled {
  cursor: not-allowed;
  opacity: .55;
}

.quick-deal-success {
  padding: 34px 20px 16px;
  text-align: center;
}

.quick-deal-success-icon {
  display: grid;
  width: 54px;
  height: 54px;
  margin: 0 auto 13px;
  place-items: center;
  border-radius: 50%;
  background: rgba(32, 161, 52, .12);
  color: #20a134;
  font-size: 25px;
  font-weight: 900;
}

.quick-deal-success-summary {
  display: flex;
  max-width: 520px;
  margin: 22px auto 0;
  padding: 16px;
  flex-direction: column;
  gap: 4px;
  border: 1px solid var(--app-border);
  border-radius: 13px;
  background: var(--app-surface-muted);
  color: var(--app-text-muted);
  font-size: 12px;
}

.quick-deal-success-summary strong {
  color: var(--app-text);
  font-size: 22px;
}

.quick-deal-success-actions {
  justify-content: center;
}

@media (max-width: 720px) {
  .quick-deal-content {
    padding: 20px;
  }

  .quick-deal-grid {
    grid-template-columns: 1fr;
  }

  .quick-deal-field--wide {
    grid-column: auto;
  }

  .quick-deal-footer,
  .quick-deal-success-actions {
    flex-direction: column-reverse;
  }

  .quick-deal-record-upload {
    align-items: stretch;
    flex-direction: column;
  }

  .quick-deal-selected-file {
    max-width: none;
  }
}
</style>
